/**
 * Theme state (§18) — tri-state: System / Light / Dark. The no-flash inline
 * script in index.html resolves <html data-theme> before first paint from the
 * stored MODE (or System → OS). This provider owns it at runtime: instant swap,
 * the chosen mode persists per device, and in System mode it tracks the OS live.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type ThemeMode = "system" | "light" | "dark";
export type Theme = "light" | "dark";
const STORAGE_KEY = "cairn.theme";

function osTheme(): Theme {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}
function rootTheme(): Theme {
  const t = typeof document !== "undefined" ? document.documentElement.dataset.theme : undefined;
  return t === "light" ? "light" : "dark";
}
function storedMode(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" || v === "system" ? v : "system";
  } catch {
    return "system";
  }
}
function resolve(mode: ThemeMode): Theme {
  return mode === "system" ? osTheme() : mode;
}

interface ThemeApi {
  /** The user's choice: system | light | dark. */
  mode: ThemeMode;
  /** The resolved theme actually applied. */
  theme: Theme;
  setMode: (m: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeApi | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => storedMode());
  const [theme, setTheme] = useState<Theme>(() => rootTheme());
  const modeRef = useRef(mode);
  modeRef.current = mode;

  const applyTheme = useCallback((t: Theme) => {
    setTheme(t);
    if (typeof document !== "undefined") document.documentElement.dataset.theme = t;
  }, []);

  const setMode = useCallback(
    (m: ThemeMode) => {
      setModeState(m);
      applyTheme(resolve(m));
      try {
        localStorage.setItem(STORAGE_KEY, m);
      } catch {
        // private mode — applies for the session only.
      }
    },
    [applyTheme],
  );

  // In System mode, track the OS preference live.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => {
      if (modeRef.current === "system") applyTheme(osTheme());
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [applyTheme]);

  const value = useMemo<ThemeApi>(() => ({ mode, theme, setMode }), [mode, theme, setMode]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Safe outside a provider (returns current root theme + no-op setter). */
export function useTheme(): ThemeApi {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx;
  return { mode: storedMode(), theme: rootTheme(), setMode: () => undefined };
}
